import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { format, addDays } from 'date-fns';
import {
  ArrowLeft, Plus, Trash2, User, FileText, Calendar, DollarSign,
  Search, X, Check, Briefcase, Home, Percent, CreditCard, AlertCircle
} from 'lucide-react';
import { generateInvoiceNumber, lineItemTypes } from '../utils/invoiceData';
import { paymentTermsOptions } from '../utils/sampleData';
import './InvoiceNew.css';

function InvoiceNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state, addInvoice, getClientById, getEstimateById, getJobById } = useApp();

  // Pre-populate from URL params
  const preselectedClientId = searchParams.get('clientId');
  const preselectedEstimateId = searchParams.get('estimateId');
  const preselectedJobId = searchParams.get('jobId');

  // Form state
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientSearch, setShowClientSearch] = useState(false);
  
  const [invoiceDate, setInvoiceDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [paymentTerms, setPaymentTerms] = useState('due_on_receipt');
  const [dueDate, setDueDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  const [lineItems, setLineItems] = useState([]);
  const [notes, setNotes] = useState('');
  
  // Deposit/Payment schedule
  const [requestDeposit, setRequestDeposit] = useState(false);
  const [depositType, setDepositType] = useState('percent'); // 'percent' or 'fixed'
  const [depositValue, setDepositValue] = useState(50);
  const [depositDueDate, setDepositDueDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Load preselected data
  useEffect(() => {
    if (preselectedClientId) {
      const client = getClientById(preselectedClientId);
      if (client) {
        setSelectedClient(client);
        setPaymentTerms(client.paymentTerms || 'due_on_receipt');
        // New clients pay in full before their appointment, so no partial deposit is auto-applied.
      }
    }
    if (preselectedEstimateId) {
      const estimate = getEstimateById(preselectedEstimateId);
      if (estimate) {
        setSelectedEstimate(estimate);
        // Auto-populate line items from estimate
        const estimateLineItems = [];
        if (estimate.deepCleanTotal && !estimate.skipDeepClean) {
          estimateLineItems.push({
            id: `li_${Date.now()}_dc`,
            type: 'service',
            description: 'Initial Deep Clean',
            quantity: 1,
            unitPrice: estimate.deepCleanTotal,
            total: estimate.deepCleanTotal
          });
        }
        if (estimate.recurringTotal) {
          const freqLabel = {
            weekly: 'Weekly Cleaning',
            biweekly: 'Bi-Weekly Cleaning',
            monthly: 'Monthly Cleaning',
            one_time: 'One-Time Cleaning'
          }[estimate.calculatorInputs?.frequency] || 'Cleaning Service';
          
          estimateLineItems.push({
            id: `li_${Date.now()}_rec`,
            type: 'service',
            description: freqLabel,
            quantity: 1,
            unitPrice: estimate.recurringTotal,
            total: estimate.recurringTotal
          });
        }
        if (estimateLineItems.length > 0) {
          setLineItems(estimateLineItems);
        }
        // Set client from estimate
        if (estimate.clientId && !preselectedClientId) {
          const client = getClientById(estimate.clientId);
          if (client) {
            setSelectedClient(client);
            setPaymentTerms(client.paymentTerms || 'due_on_receipt');
          }
        }
      }
    }
    if (preselectedJobId) {
      const job = getJobById(preselectedJobId);
      if (job) {
        setSelectedJob(job);
        // Auto-populate line item from job
        setLineItems([{
          id: `li_${Date.now()}_job`,
          type: 'service',
          description: `Cleaning Service - ${job.address}`,
          quantity: 1,
          unitPrice: job.price || 0,
          total: job.price || 0
        }]);
        // Set client from job
        if (job.clientId && !preselectedClientId) {
          const client = getClientById(job.clientId);
          if (client) {
            setSelectedClient(client);
            setPaymentTerms(client.paymentTerms || 'due_on_receipt');
          }
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedClientId, preselectedEstimateId, preselectedJobId, getClientById, getEstimateById, getJobById]);

  // Update due date when payment terms or invoice date changes
  useEffect(() => {
    const terms = paymentTermsOptions.find(t => t.value === paymentTerms);
    const days = terms?.days || 0;
    const newDueDate = addDays(new Date(invoiceDate), days);
    setDueDate(format(newDueDate, 'yyyy-MM-dd'));
  }, [paymentTerms, invoiceDate]);

  // Filter clients for search
  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return state.clients.slice(0, 10);
    const search = clientSearch.toLowerCase();
    return state.clients.filter(c => 
      `${c.firstName} ${c.lastName}`.toLowerCase().includes(search) ||
      c.email?.toLowerCase().includes(search) ||
      c.phone?.includes(search)
    ).slice(0, 10);
  }, [state.clients, clientSearch]);

  // Get client's estimates and jobs for linking
  const clientEstimates = useMemo(() => {
    if (!selectedClient) return [];
    return state.estimates.filter(e => 
      e.clientId === selectedClient.id && 
      (e.status === 'approved' || e.status === 'pending')
    );
  }, [selectedClient, state.estimates]);

  const clientJobs = useMemo(() => {
    if (!selectedClient) return [];
    return state.jobs.filter(j => 
      j.clientId === selectedClient.id &&
      j.status === 'completed'
    ).slice(0, 10);
  }, [selectedClient, state.jobs]);

  // Calculate totals
  const subtotal = useMemo(() => {
    return lineItems.reduce((sum, item) => sum + (item.total || 0), 0);
  }, [lineItems]);

  const depositAmount = useMemo(() => {
    if (!requestDeposit) return 0;
    if (depositType === 'percent') {
      return Math.round(subtotal * (depositValue / 100) * 100) / 100;
    }
    return depositValue;
  }, [requestDeposit, depositType, depositValue, subtotal]);

  const balanceAfterDeposit = subtotal - depositAmount;

  // Line item handlers
  const addLineItem = () => {
    setLineItems([...lineItems, {
      id: `li_${Date.now()}`,
      type: 'service',
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0
    }]);
  };

  const updateLineItem = (id, field, value) => {
    setLineItems(lineItems.map(item => {
      if (item.id !== id) return item;
      const updated = { ...item, [field]: value };
      // Recalculate total
      if (field === 'quantity' || field === 'unitPrice') {
        updated.total = (updated.quantity || 0) * (updated.unitPrice || 0);
      }
      return updated;
    }));
  };

  const removeLineItem = (id) => {
    setLineItems(lineItems.filter(item => item.id !== id));
  };

  // Check if client is new (no paid invoices yet)
  const isNewClient = (client) => {
    if (!client) return false;
    const clientInvoices = state.invoices.filter(
      inv => inv.clientId === client.id && inv.status === 'paid'
    );
    return clientInvoices.length === 0;
  };

  // Select client
  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setClientSearch('');
    setShowClientSearch(false);
    setPaymentTerms(client.paymentTerms || 'due_on_receipt');
    // Clear linked estimate/job when changing client
    setSelectedEstimate(null);
    setSelectedJob(null);
    // New clients pay in full before their appointment, so no partial deposit is auto-applied.
    setRequestDeposit(false);
  };

  // Save invoice
  const handleSave = async (sendAfterSave = false) => {
    if (!selectedClient) {
      setError('Please select a client');
      return;
    }
    if (lineItems.length === 0) {
      setError('Please add at least one line item');
      return;
    }
    if (lineItems.some(item => !item.description)) {
      setError('All line items must have a description');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const invoice = {
        id: `inv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        invoiceNumber: generateInvoiceNumber(),
        clientId: selectedClient.id,
        clientName: `${selectedClient.firstName || ''} ${selectedClient.lastName || ''}`.trim() || 'Unknown',
        clientEmail: selectedClient.accountingEmail || selectedClient.email || null,
        clientPhone: selectedClient.phone || null,
        estimateId: selectedEstimate?.id || null,
        jobId: selectedJob?.id || null,
        propertyAddress: selectedJob?.fullAddress || selectedJob?.address || selectedEstimate?.propertyAddress || '',
        status: 'draft',
        invoiceDate,
        dueDate,
        paymentTerms,
        lineItems,
        subtotal,
        tax: 0,
        total: subtotal,
        // Deposit info
        requestDeposit,
        depositType: requestDeposit ? depositType : null,
        depositValue: requestDeposit ? depositValue : null,
        depositAmount: requestDeposit ? depositAmount : 0,
        depositDueDate: requestDeposit ? depositDueDate : null,
        depositPaid: false,
        isFirstCleanInvoice: requestDeposit && selectedClient ? isNewClient(selectedClient) : false,
        // Payment tracking
        tipAmount: 0,
        amountPaid: 0,
        balance: subtotal,
        // Notes
        notes: notes || '',
        // Stripe fields
        stripeInvoiceId: null,
        stripePaymentUrl: null,
        stripeCustomerId: selectedClient.stripeCustomerId || null,
        // Timestamps
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        sentAt: null,
        paidAt: null
      };

      console.log('Attempting to create invoice for client:', selectedClient.id);
      
      await addInvoice(invoice);
      
      console.log('Invoice created successfully, navigating to:', invoice.id);
      
      if (sendAfterSave) {
        navigate(`/admin/invoices/${invoice.id}?send=true`);
      } else {
        navigate(`/admin/invoices/${invoice.id}`);
      }
    } catch (err) {
      console.error('Failed to create invoice:', err);
      setError(`Failed to create invoice: ${err.message || 'Unknown error'}. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="invoice-new-page">
      {/* Header */}
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/admin/invoices')}>
          <ArrowLeft size={20} />
          Back to Invoices
        </button>
        <h1>Create Invoice</h1>
      </div>

      {error && (
        <div className="error-banner">
          <AlertCircle size={18} />
          {error}
          <button onClick={() => setError(null)}><X size={16} /></button>
        </div>
      )}

      <div className="invoice-form">
        {/* Client Selection */}
        <section className="form-section">
          <h2><User size={20} /> Client</h2>
          
          {selectedClient ? (
            <div className="selected-client">
              <div className="client-info">
                <strong>{selectedClient.firstName} {selectedClient.lastName}</strong>
                <span>{selectedClient.email}</span>
                <span>{selectedClient.phone}</span>
              </div>
              <button className="btn-change" onClick={() => {
                setSelectedClient(null);
                setShowClientSearch(true);
              }}>
                Change
              </button>
            </div>
          ) : (
            <div className="client-search-container">
              <div className="search-input-wrapper">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search clients by name, email, or phone..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  onFocus={() => setShowClientSearch(true)}
                />
              </div>
              
              {showClientSearch && (
                <div className="client-search-results">
                  {filteredClients.length === 0 ? (
                    <div className="no-results">No clients found</div>
                  ) : (
                    filteredClients.map(client => (
                      <div 
                        key={client.id} 
                        className="client-result"
                        onClick={() => handleSelectClient(client)}
                      >
                        <strong>{client.firstName} {client.lastName}</strong>
                        <span>{client.email || client.phone}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </section>

        {/* Link to Estimate or Job */}
        {selectedClient && (
          <section className="form-section">
            <h2><FileText size={20} /> Link to Estimate or Job (Optional)</h2>
            
            <div className="link-options">
              {/* Estimates */}
              {clientEstimates.length > 0 && (
                <div className="link-group">
                  <label>Link to Estimate</label>
                  <select 
                    value={selectedEstimate?.id || ''} 
                    onChange={(e) => {
                      const est = clientEstimates.find(e => e.id === e.target.value);
                      setSelectedEstimate(est || null);
                      if (est) setSelectedJob(null);
                    }}
                  >
                    <option value="">-- None --</option>
                    {clientEstimates.map(est => (
                      <option key={est.id} value={est.id}>
                        {est.propertyAddress} - ${est.recurringTotal || est.deepCleanTotal || 0}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Jobs */}
              {clientJobs.length > 0 && (
                <div className="link-group">
                  <label>Link to Completed Job</label>
                  <select 
                    value={selectedJob?.id || ''} 
                    onChange={(e) => {
                      const job = clientJobs.find(j => j.id === e.target.value);
                      setSelectedJob(job || null);
                      if (job) setSelectedEstimate(null);
                    }}
                  >
                    <option value="">-- None --</option>
                    {clientJobs.map(job => (
                      <option key={job.id} value={job.id}>
                        {job.date} - {job.address} - ${job.price || 0}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Invoice Details */}
        <section className="form-section">
          <h2><Calendar size={20} /> Invoice Details</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Invoice Date</label>
              <input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
              />
            </div>
            
            <div className="form-group">
              <label>Payment Terms</label>
              <select 
                value={paymentTerms} 
                onChange={(e) => setPaymentTerms(e.target.value)}
              >
                {paymentTermsOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label>Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* Line Items */}
        <section className="form-section">
          <div className="section-header">
            <h2><DollarSign size={20} /> Line Items</h2>
            <button className="btn-add-item" onClick={addLineItem}>
              <Plus size={16} /> Add Item
            </button>
          </div>
          
          <div className="line-items-table">
            <div className="table-header">
              <span className="col-type">Type</span>
              <span className="col-desc">Description</span>
              <span className="col-qty">Qty</span>
              <span className="col-price">Unit Price</span>
              <span className="col-total">Total</span>
              <span className="col-action"></span>
            </div>
            
            {lineItems.length === 0 ? (
              <div className="no-items">
                No line items yet. Click "Add Item" to get started.
              </div>
            ) : (
              lineItems.map(item => (
                <div key={item.id} className="table-row">
                  <select
                    className="col-type"
                    value={item.type}
                    onChange={(e) => updateLineItem(item.id, 'type', e.target.value)}
                  >
                    {Object.entries(lineItemTypes).map(([key, val]) => (
                      <option key={key} value={key}>{val.label}</option>
                    ))}
                  </select>
                  
                  <input
                    className="col-desc"
                    type="text"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                  />
                  
                  <input
                    className="col-qty"
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => updateLineItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                  />
                  
                  <div className="col-price price-input">
                    <span>$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unitPrice}
                      onChange={(e) => updateLineItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  
                  <span className="col-total">${item.total.toFixed(2)}</span>
                  
                  <button 
                    className="col-action btn-remove"
                    onClick={() => removeLineItem(item.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))
            )}
            
            <div className="table-footer">
              <span className="subtotal-label">Subtotal:</span>
              <span className="subtotal-value">${subtotal.toFixed(2)}</span>
            </div>
          </div>
        </section>

        {/* Deposit / Payment Schedule */}
        <section className="form-section">
          <h2><CreditCard size={20} /> Payment Schedule</h2>
          
          <div className="deposit-toggle">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={requestDeposit}
                onChange={(e) => setRequestDeposit(e.target.checked)}
              />
              <span className="toggle-slider"></span>
              Request Deposit
            </label>
            {selectedClient && isNewClient(selectedClient) && (
              <span className="new-client-deposit-hint">
                <AlertCircle size={14} />
                New client — full payment due before the appointment per policy
              </span>
            )}
          </div>
          
          {requestDeposit && (
            <div className="deposit-options">
              <div className="form-row">
                <div className="form-group">
                  <label>Deposit Type</label>
                  <div className="radio-group">
                    <label className={depositType === 'percent' ? 'active' : ''}>
                      <input
                        type="radio"
                        name="depositType"
                        value="percent"
                        checked={depositType === 'percent'}
                        onChange={() => setDepositType('percent')}
                      />
                      <Percent size={16} />
                      Percentage
                    </label>
                    <label className={depositType === 'fixed' ? 'active' : ''}>
                      <input
                        type="radio"
                        name="depositType"
                        value="fixed"
                        checked={depositType === 'fixed'}
                        onChange={() => setDepositType('fixed')}
                      />
                      <DollarSign size={16} />
                      Fixed Amount
                    </label>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>{depositType === 'percent' ? 'Deposit Percentage' : 'Deposit Amount'}</label>
                  <div className="deposit-input">
                    {depositType === 'fixed' && <span>$</span>}
                    <input
                      type="number"
                      min="0"
                      max={depositType === 'percent' ? 100 : subtotal}
                      value={depositValue}
                      onChange={(e) => setDepositValue(parseFloat(e.target.value) || 0)}
                    />
                    {depositType === 'percent' && <span>%</span>}
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Deposit Due Date</label>
                  <input
                    type="date"
                    value={depositDueDate}
                    onChange={(e) => setDepositDueDate(e.target.value)}
                  />
                </div>
              </div>
              
              <div className="deposit-summary">
                <div className="summary-row">
                  <span>Deposit Amount:</span>
                  <span className="amount">${depositAmount.toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span>Balance Due on Completion:</span>
                  <span className="amount">${balanceAfterDeposit.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Notes */}
        <section className="form-section">
          <h2>Notes (Optional)</h2>
          <textarea
            placeholder="Add any notes for this invoice..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
          />
        </section>

        {/* Actions */}
        <div className="form-actions">
          <button 
            className="btn-secondary" 
            onClick={() => navigate('/admin/invoices')}
            disabled={saving}
          >
            Cancel
          </button>
          <button 
            className="btn-primary" 
            onClick={() => handleSave(false)}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save as Draft'}
          </button>
          <button 
            className="btn-success" 
            onClick={() => handleSave(true)}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save & Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default InvoiceNew;
